import { PortalType } from "generated-graphql/graphql";
import { GetTierIiReportQuery } from "generated-graphql/graphql";

type PortalCredentialItem = NonNullable<
  GetTierIiReportQuery["tierIIReport"]["facility"]["portalCredentials"]
>[number];

type WorkflowActivity = NonNullable<
  GetTierIiReportQuery["tierIIReport"]["currentWorkflow"]
>["activities"][number];

/**
 * Heuristic: activity description suggests an LEPC/county portal (e.g. "Fairfax County Portal Automation", "Henrico County Portal").
 */
function isLepcPortalActivity(
  activity: WorkflowActivity | null | undefined
): boolean {
  const desc = activity?.description?.toLowerCase() ?? "";
  return (
    desc.includes("lepc") ||
    (desc.includes("county") && desc.includes("portal"))
  );
}

/**
 * Returns the state (non-LEPC) portal credential from the list, for use in state-only flows like E-Plan payment.
 */
export function getStatePortalCredential(
  portalCredentials: PortalCredentialItem[] | null | undefined
): PortalCredentialItem | undefined {
  if (!portalCredentials?.length) return undefined;
  return (
    portalCredentials.find((pc) => pc?.type !== PortalType.Lepc) ?? undefined
  );
}

/**
 * Returns the portal credential relevant to the current workflow activity:
 * - When the current activity is an LEPC portal activity → LEPC credential
 * - When the workflow includes state portal automation → state credential
 * - With one credential, returns it; with two, picks by activity.
 */
export function getRelevantPortalCredential(
  portalCredentials: PortalCredentialItem[] | null | undefined,
  currentActivity: WorkflowActivity | null | undefined
): PortalCredentialItem | undefined {
  if (!portalCredentials?.length) return undefined;
  if (portalCredentials.length === 1) return portalCredentials[0] ?? undefined;

  const lepcCredential = portalCredentials.find(
    (pc) => pc?.type === PortalType.Lepc
  );
  const stateCredential = portalCredentials.find(
    (pc) => pc?.type !== PortalType.Lepc
  );

  if (isLepcPortalActivity(currentActivity) && lepcCredential) {
    return lepcCredential;
  }
  return stateCredential ?? lepcCredential ?? portalCredentials[0];
}
